import type { VerseTableRow } from '@/interfaces/ExplorerTableRow';

/** Alle statistischen Spalten (ohne sura/verse) */
export type StatKey =
  | 'readings_count'
  | 'references_count'
  | 'sources_count'
  | 'variants_count'
  | 'manuscripts_count'
  | 'intertexts_count';

/** Router-Location-Typ für typsichere Link-Generation */
export type RouterLocation = { name: string; params: Record<string, unknown> };

/** Erweiterte Metadaten für Spalten mit expliziten Flags */
export interface ExplorerColumn {
  key: keyof VerseTableRow;
  labelKey: string;
  /** numerische Grenzen für Slider-Filter (optional) */
  min?: number;
  max?: number;
  /** Standard-Minimalwert für Filter */
  defaultMin?: number;
  /** Standard-Maximalwert für Filter */
  defaultMax?: number;
  /** Explizit filterbare Spalte */
  isFilterable?: boolean;
  /** Statistik-Spalte (nicht sura/verse) */
  isStat?: boolean;
  /** Link-Generation für Router */
  linkGenerator?: (row: VerseTableRow) => RouterLocation;
  /** Optionaler Daten-Accessor für berechnete Felder */
  dataAccessor?: (row: VerseTableRow) => number;
}

// Generische Link-Factories zur Reduzierung von Duplikation
const makeVerseLink = (routeName: string) => (row: VerseTableRow): RouterLocation => ({
  name: routeName,
  params: { sura: row.sura, verse: row.verse },
});

const variantLink = makeVerseLink('VerseVariants');
const manuscriptLink = makeVerseLink('VerseManuscriptResults');
const intertextLink = makeVerseLink('VerseIntertextResults');
const versePrintLink = makeVerseLink('VersePrint');

export const EXPLORER_COLUMNS: readonly ExplorerColumn[] = [
  {
    key: 'sura',
    labelKey: 'global.sura',
    min: 1,
    max: 114,
    isFilterable: true,
  },
  {
    key: 'verse',
    labelKey: 'global.verse',
    min: 1,
    max: 286,
    isFilterable: true,
  },
  {
    key: 'readings_count',
    labelKey: 'explorer.readings',
    defaultMin: 8,
    isFilterable: true,
    isStat: true,
    linkGenerator: variantLink,
  },
  {
    key: 'references_count',
    labelKey: 'explorer.references',
    isFilterable: true,
    isStat: true,
    linkGenerator: variantLink,
  },
  {
    key: 'sources_count',
    labelKey: 'explorer.sources',
    isFilterable: true,
    isStat: true,
    linkGenerator: variantLink,
  },
  {
    key: 'variants_count',
    labelKey: 'explorer.variants',
    isFilterable: true,
    isStat: true,
    linkGenerator: variantLink,
  },
  {
    key: 'manuscripts_count',
    labelKey: 'navigation.manuscripts',
    isFilterable: true,
    isStat: true,
    linkGenerator: manuscriptLink,
  },
  {
    key: 'intertexts_count',
    labelKey: 'explorer.intertexts',
    isFilterable: true,
    isStat: true,
    linkGenerator: intertextLink,
  },
];

/** Präziser Typ basierend auf tatsächlich vorhandenen Spalten */
export type ColumnKey = typeof EXPLORER_COLUMNS[number]['key'];

// Cache für Performance-kritische Operationen
const valueAccessorCache: Map<ColumnKey, (row: VerseTableRow) => number> = new Map();

/** Optimierte Helper-Funktion mit Caching für Spalten-Werte */
export function getColumnValue(row: VerseTableRow, key: ColumnKey): number {
  if (!valueAccessorCache.has(key)) {
    const column = EXPLORER_COLUMNS.find((col) => col.key === key);

    if (column?.dataAccessor) {
      valueAccessorCache.set(key, column.dataAccessor);
    } else {
      // Umbenennung des Parameters, um Shadowing zu vermeiden
      valueAccessorCache.set(key, (r) => (r as any)[key] ?? 0);
    }
  }

  // Non-null assertion entfernt, stattdessen Fallback auf 0
  const accessor = valueAccessorCache.get(key);
  return accessor ? accessor(row) : 0;
}

/** Alle filterbaren Spalten - explizit über Flag bestimmt */
export const FILTERABLE_COLUMNS = EXPLORER_COLUMNS.filter((col) => col.isFilterable === true);

/** Statistik-Spalten - explizit über Flag bestimmt */
export const STAT_COLUMNS = EXPLORER_COLUMNS.filter((col) => col.isStat === true);

/** Helper-Funktion zur Ermittlung der Spalten-Konfiguration */
export function getColumnConfig(key: ColumnKey): ExplorerColumn | undefined {
  return EXPLORER_COLUMNS.find((col) => col.key === key);
}

/** Helper-Funktion für Standard-Filter-Werte */
export function getDefaultFilterValue(column: ExplorerColumn, maxValue: number): [number, number] {
  const defaultMin = column.defaultMin ?? column.min ?? 0;
  const defaultMax = column.defaultMax ?? maxValue;
  return [defaultMin, defaultMax];
}
